/**
 * Stock On Hand, a tab on Reports for a business that keeps goods (migration
 * 0086; the inventory plan's §7). Each pool's quantity and value as of the
 * day the Reports period ends, by item, account, fund and program, and each
 * stock account's balance beside the pools on it.
 *
 * THE TIE IS SHOWN, NOT ASSUMED. The two are equal by construction: every
 * line on a stock account names an item, and every stock credit is the sum of
 * what it drew. Printing both is what lets a reader see that rather than take
 * it on trust, as the non-profit statements show their own ties.
 */

import type { StockOnHand } from '../api.js';
import { formatCents } from '../lib/money.js';
import { showQuantity } from '../pages/ItemsPage.js';
import { InfoNote } from './InfoNote.js';

export function StockOnHandReport({ report }: { report: StockOnHand }) {
  const byFund = report.pools.some((pool) => pool.fundName !== null);
  const byProject = report.pools.some((pool) => pool.projectName !== null);
  return (
    <div className="report">
      <h2>Stock On Hand</h2>
      <p className="muted">
        As of {report.asOf}.{' '}
        <InfoNote label="How Stock Is Valued">
          Each layer of goods is valued at what it cost when it came in: what was paid, or, for goods given to the
          business, their value when received. Goods leave first in, first out, so what is left is the most recent
          layers. A stock account&apos;s balance is the value of the goods on it, which the last table shows.
        </InfoNote>
      </p>
      {report.pools.length === 0 ? (
        <p className="muted">No goods on hand on {report.asOf}.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines" aria-label="Stock On Hand">
            <thead>
              <tr>
                <th>Item</th>
                <th>Account</th>
                {byFund && <th>Fund</th>}
                {byProject && <th>Program Or Project</th>}
                <th className="num">On Hand</th>
                <th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {report.pools.map((pool) => (
                <tr key={`${pool.itemId}-${pool.accountId}-${pool.fundId ?? ''}-${pool.projectId ?? ''}`}>
                  <td>
                    {pool.item}
                    {pool.sku !== null && <span className="muted"> · {pool.sku}</span>}
                  </td>
                  <td>
                    {pool.accountCode} {pool.accountName}
                  </td>
                  {byFund && <td>{pool.fundName ?? ''}</td>}
                  {byProject && <td>{pool.projectName ?? ''}</td>}
                  <td className="num">
                    {showQuantity(pool.quantity)} {pool.unit}
                  </td>
                  <td className="num">{formatCents(pool.valueCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3>Stock Accounts</h3>
      <div className="table-scroll">
        <table className="lines" aria-label="Stock Accounts">
          <thead>
            <tr>
              <th>Account</th>
              <th className="num">Balance</th>
              <th className="num">Goods On It</th>
              <th className="num">Difference</th>
            </tr>
          </thead>
          <tbody>
            {report.accounts.map((account) => (
              <tr key={account.accountId}>
                <td>
                  {account.code} {account.name}
                </td>
                <td className="num">{formatCents(account.balanceCents)}</td>
                <td className="num">{formatCents(account.poolsCents)}</td>
                <td className="num">{formatCents(account.balanceCents - account.poolsCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
